import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal } from "react-bootstrap";
import api from "../../../../Services/api";
import Loader from "react-spinner-loader";
import "./CreateUser.css";
import FormHandler from "../../Common/Form/FormHandler/FormHandler";
import { toast } from "react-toastify";
import { useKeycloak } from "@react-keycloak/web";

import {
  keycloakbaseurl,
  createJobTitle,
  getJobTitle,
  getAllRole,
  createUserintoRealm,
  getUserDataByEmailAttr,
  updateUserByUserId,
  updateUserInAppURL,
  assignRoleToUser,
  getallbranchnames,
  getallBanksByRole,
  getThemeURL,
  getRegionByRole,
  departmentslist,
  validateUserNames,
} from "../../../../Services/EndPoints";
import {
  schema,
  CreateUserConfig,
} from "../../../../JsonData/CreateUserConfig";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  postDataWithBody,
  getData,
  putDataWithBody,
} from "../../../../Services/Services";
import ModalResourceType from "../../../../Components/UiComponents/ModalResourceType/ModalResourceType";
import { useTranslation } from "react-i18next";

// Email is derived from the User ID, always lowercase (e.g. "FleetAdmin" -> "fleetadmin@bscb.com")
const toUserEmail = (userName) => `${(userName || "").trim().toLowerCase()}@bscb.com`;

// Component for user registration and editing with multi-step form and bank/branch access control
const UserRegistrationForm = (props) => {
  const { keycloak } = useKeycloak();
  const [userConfig, setUserConfig] = useState(CreateUserConfig);
  const [regionDataFrUpsert, setRegionDataFrUpsert] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [regionChanged, isRegionChanged] = useState(false);
  const [jobTitleList, setJobTitleList] = useState({});
  const [loader, setLoader] = useState(false);
  const [jobPopUp, setJobPopUp] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const { state } = useLocation();
  const { t } = useTranslation();
  const [componentKey, setComponentKey] = useState(0);

  const [adminAccess, setAdminAccess] = useState("false");
  const [
    makeRadioButtonForBranchUserDisable,
    setmakeRadioButtonForBranchUserDisable,
  ] = useState(false);
  const [roleTrue, setRoleTrue] = useState(false);
  const [userNameDisable, setUserNameDisable] = useState(false);
  const location = useLocation();
  const { isBankFieldDisable } = location.state || {};
  const [isAdminRole, setIsAdminRole] = useState(false);
  const [userLevelDisabled, setUserLevelDisabled] = useState(false);
  // Logged-in user's app_type (e.g. "FLEET"), sent as appType in the create/update payload
  const [appType, setAppType] = useState(null);

  let dataFrEdit = state?.data;
  let edit = state?.edit;
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    reset,
    control,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema[activeTab]),
    mode: "all",
    context: { edit: !!dataFrEdit },
  });

  // ===== HELPER FUNCTION - ADD THIS AT THE TOP =====
  const getFieldIndexByName = (fieldName) => {
    return CreateUserConfig[0].fields.findIndex(field => field.name === fieldName);
  };
  // =================================================

  useEffect(() => {
    if (dataFrEdit) {
      setRoleTrue(true);
      setUserLevelDisabled(!dataFrEdit.grv_status);
    }
  }, [dataFrEdit]);

  useEffect(() => {
    if (dataFrEdit) {
      setUserNameDisable(true);
    }
  }, [dataFrEdit]);

  useEffect(() => {
    getUserProfile();
    getInitFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FIXED: Watch admin_access field changes
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "admin_access") {
        setAdminAccess(value.admin_access);
        const updatedConfig = [...CreateUserConfig];

        // FIXED: Find branch field by name instead of hardcoded index
        const branchFieldIndex = getFieldIndexByName('all_branches');
        updatedConfig[0].fields[branchFieldIndex]["disabled"] = value.admin_access === "true";

        if (value.admin_access === "true") {
          setValue("all_branches", "");
          updatedConfig[0].fields[branchFieldIndex]["options"] = [];
        } else if (value.all_banks) {
          fetchBranchDetails(value.all_banks);
        }

        setUserConfig(updatedConfig);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, setValue]);

  useEffect(() => {
    if (!dataFrEdit) {
      setValue("admin_access", "true");
      const updatedConfig = [...CreateUserConfig];
      
      // FIXED: Find branch field by name
      const branchFieldIndex = getFieldIndexByName('all_branches');
      updatedConfig[0].fields[branchFieldIndex]["disabled"] = true;
      setUserConfig(updatedConfig);
    }
  }, [setValue, dataFrEdit]);

  const getInitFormData = async (profileData = null) => {
    setLoader(true);

    try {
      await fetchAllRoles(profileData);
      await getUserProfileForRegion();
      await getAllJobTitleList();
      await getAllDepartmentList();

      if (dataFrEdit) {
        setTimeout(async () => {
          await populateUserFormFrEdit(dataFrEdit);
        }, 100);
      }
    } catch (error) {
      console.error("Error in getInitFormData:", error);
    } finally {
      setLoader(false);
    }
  };

  const getUserProfile = async () => {
    try {
      const userProfileData = await getData(getThemeURL);
      if (userProfileData && userProfileData.length > 0) {
        setAppType(userProfileData[0].app_type || null);
        fetchBanks(userProfileData[0]);
        await getInitFormData(userProfileData[0]);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  const createPayload = (profile) => {
    let admin_role = "U";

    if (profile.is_admin === true) {
      admin_role = "HO";
    } else if (profile.role_name === "SUPER ADMIN") {
      admin_role = "SA";
    }

    return {
      admin_role: admin_role,
      bank_id: profile.bank_id || null,
      branch_id: profile.branch_id || null,
    };
  };

  // FIXED: fetchBanks function
  const fetchBanks = async (profile) => {
    try {
      const payload = createPayload(profile);
      const response = await postDataWithBody(getallBanksByRole, payload, {});
      if (response && Array.isArray(response)) {
        const formattedBanks = response.map((bank) => ({
          value: bank.id,
          label: bank.bankName,
        }));

        // FIXED: Find bank field by name
        const bankFieldIndex = getFieldIndexByName('all_banks');
        CreateUserConfig[0].fields[bankFieldIndex]["options"] = formattedBanks;
        setUserConfig(CreateUserConfig);
      } else {
        toast.error("Failed to load banks data");
        console.error("Invalid banks data:", response);
      }
    } catch (error) {
      toast.error("Failed to fetch banks");
      console.error("Fetch error:", error);
      throw error;
    }
  };

  const selectedbankid = watch("all_banks");
  const selectedRole = watch("role_list");

  useEffect(() => {
    if (selectedRole) {
      let roleName = "";
      let isAdmin = false;

      if (typeof selectedRole === "object" && selectedRole !== null) {
        roleName = selectedRole.name || selectedRole.label || "";
        isAdmin = selectedRole.is_admin || false;
      } else if (typeof selectedRole === "string") {
        // FIXED: Find role field by name
        const roleFieldIndex = getFieldIndexByName('role_list');
        const roleOptions = CreateUserConfig[0]?.fields[roleFieldIndex]?.options || [];
        const foundRole = roleOptions.find(
          (role) =>
            role.id === selectedRole ||
            String(role.value) === String(selectedRole)
        );
        roleName = foundRole ? foundRole.name || foundRole.label || "" : "";
        isAdmin = foundRole ? foundRole.is_admin || false : false;
      }

      setIsAdminRole(isAdmin);

      if (roleName === "HO") {
        setmakeRadioButtonForBranchUserDisable(true);
      } else {
        setmakeRadioButtonForBranchUserDisable(false);
      }
    }
  }, [selectedRole]);

  useEffect(() => {
    if (selectedbankid && adminAccess === "false" && !dataFrEdit) {
      fetchBranchDetails(selectedbankid);
    } else if (adminAccess === "true" && !dataFrEdit) {
      setValue("all_branches", "");
      const updatedConfig = [...CreateUserConfig];
      
      // FIXED: Find branch field by name
      const branchFieldIndex = getFieldIndexByName('all_branches');
      updatedConfig[0].fields[branchFieldIndex]["options"] = [];
      setUserConfig(updatedConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedbankid, adminAccess, dataFrEdit]);

  // FIXED: fetchBranchDetails function
  const fetchBranchDetails = async (bankId) => {
    setLoader(true);
    try {
      const selectedBankId = bankId || selectedbankid;
      if (!selectedBankId) return;
      const response = await getData(
        getallbranchnames.replace("ID", selectedBankId)
      );
      if (response && Array.isArray(response)) {
        const formattedBranches = response.map((branch) => ({
          value: branch.id,
          label: branch.branchName,
        }));
        const updatedConfig = [...CreateUserConfig];
        
        // FIXED: Find branch field by name
        const branchFieldIndex = getFieldIndexByName('all_branches');
        updatedConfig[0].fields[branchFieldIndex]["options"] = formattedBranches;
        updatedConfig[0].fields[branchFieldIndex]["disabled"] = adminAccess === "true";
        setUserConfig(updatedConfig);
        
        if (formattedBranches.length === 1) {
          setValue("all_branches", formattedBranches[0].value);
        }
      } else {
        toast.error("Failed to load branch data");
        console.error("Invalid branch data:", response);
      }
    } catch (error) {
      toast.error("Failed to fetch branches");
      console.error("Fetch error:", error);
    } finally {
      setLoader(false);
    }
  };

  // FIXED: fetchAllRoles function
  const fetchAllRoles = async (profileData = null) => {
    setLoader(true);

    try {
      let roleIdMap = {};
      let profile = profileData;
      if (!profile) {
        profile = await getUserProfile();

        if (!profile) {
          setLoader(false);
          return;
        }
      }

      const payload = {
        name: profile.role_name,
        bankId: profile.bank_id || null,
      };

      const rolearr = await postDataWithBody(getAllRole, payload, {});
      let filteredRoles = rolearr;

      filteredRoles.forEach(function (role) {
        roleIdMap[role.id] = role;
        role.label = role.name;
        role.app_role_id = role.id;
        role.value = role.app_role_id;
        role.id = role.app_role_id;
      });

      const rolesWithDefault = [
        { value: "", label: "Select a Role", id: "", name: "Select a Role" },
        ...filteredRoles,
      ];
      
      // FIXED: Find role field by name
      const roleFieldIndex = getFieldIndexByName('role_list');
      CreateUserConfig[0].fields[roleFieldIndex]["options"] = rolesWithDefault;
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to fetch roles", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setLoader(false);
    }
  };

  const getUserProfileForRegion = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      if (userProfile && userProfile.length > 0) {
        getRegionList(userProfile[0]);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  const createPayloadForRegion = (profile) => {
    let admin_role = "U";

    if (profile.is_admin === true) {
      admin_role = "HO";
    } else if (profile.role_name === "SUPER ADMIN") {
      admin_role = "SA";
    }

    return {
      admin_role: admin_role,
      userId: profile.id || null,
    };
  };

  // FIXED: getRegionList function
  const getRegionList = async (profile) => {
    try {
      const payload = createPayloadForRegion(profile);
     // let regionList = await postDataWithBody(getRegionByRole, payload, {});
      let regionList = await getData(getRegionByRole);

      if (profile.role_name === "SUPER ADMIN") {
        regionList = regionList.map((region) => {
          if (region.name === "Bihar") {
            return { ...region, name: "All districts" };
          }
          return region;
        });

        const regionMap = new Map();
        regionList.forEach((region) => {
          regionMap.set(region.id, {
            ...region,
            id: String(region.id),
            label: region.name,
            value: String(region.id),
            depth: 1,
            children: [],
          });
        });

        const roots = [];
        for (const region of regionMap.values()) {
          const parentId = region.parent_region_id;

          if (parentId && parentId !== region.id && regionMap.has(parentId)) {
            const parent = regionMap.get(parentId);
            region.depth = (parent.depth || 1) + 1;
            parent.children.push(region);
          } else {
            roots.push(region);
          }
        }

        setRegionData(roots);
        const updatedConfig = [...CreateUserConfig];
        
        // FIXED: Find region field by name
        const regionFieldIndex = getFieldIndexByName('region');
        updatedConfig[0].fields[regionFieldIndex]["options"] = roots;
        setUserConfig(updatedConfig);
      } else {
        regionList = regionList.map((region) => {
          if (region.name === "Bihar") {
            return { ...region, name: "All districts" };
          }
          return region;
        });

        setRegionData(regionList);
        let hierregionData = flatToHierarchy(regionList, "parent_region_id", 3);
        
        const updatedConfig = [...CreateUserConfig];
        // FIXED: Find region field by name
        const regionFieldIndex = getFieldIndexByName('region');
        updatedConfig[0].fields[regionFieldIndex]["options"] = hierregionData;
        setUserConfig(updatedConfig);
      }
    } catch (error) {
      console.error("Error fetching region list:", error);
      toast.error("Failed to fetch region data", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  // FIXED: getAllJobTitleList function
  const getAllJobTitleList = async () => {
    try {
      const jbTitlList = await getData(getJobTitle);
      const formattedJobTitles = jbTitlList.map((title) => ({
        value: title.id,
        label: title.value,
      }));

      const updatedConfig = [...CreateUserConfig];
      // FIXED: Find job title field by name
      const jobTitleFieldIndex = getFieldIndexByName('jobTitleName');
      updatedConfig[0].fields[jobTitleFieldIndex]["options"] = formattedJobTitles;
      setUserConfig(updatedConfig);
      setJobTitleList(formattedJobTitles);
    } catch (error) {
      console.error("Error fetching job titles:", error);
      toast.error("Failed to fetch job titles", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  const flatToHierarchy = (flat, parent_key_name, maxDepth = 3) => {
    const roots = [];
    const all = new Map();

    flat.forEach((item) => {
      all.set(item.id, {
        ...item,
        id: String(item.id),
        label: item.name,
        value: String(item.id),
        depth: 1,
        children: [],
      });
    });

    for (const item of all.values()) {
      const parentId = item[parent_key_name];

      if (
        !parentId ||
        parentId === 0 ||
        !all.has(parentId) ||
        parentId === item.id
      ) {
        roots.push(item);
      } else {
        const parent = all.get(parentId);
        item.depth = (parent.depth || 1) + 1;

        if (item.depth <= maxDepth) {
          parent.children.push(item);
        } else {
          roots.push(item);
        }
      }
    }

    return roots;
  };

  // FIXED: createJobTiltle function
  const createJobTiltle = async (jobTitle) => {
    let input = { job_title: jobTitle, status: true };
    var resourceData = await postDataWithBody(createJobTitle, input, {});
    let jbTitlTemp = [
      ...jobTitleList,
      { label: jobTitle, value: resourceData.id },
    ];

    const updatedConfig = [...CreateUserConfig];
    // FIXED: Find job title field by name
    const jobTitleFieldIndex = getFieldIndexByName('jobTitleName');
    updatedConfig[0].fields[jobTitleFieldIndex]["options"] = jbTitlTemp;
    setUserConfig(updatedConfig);
    setJobTitleList(jbTitlTemp);
  };

  // FIXED: populateUserFormFrEdit function
  const populateUserFormFrEdit = async (formdata, agencyList) => {
    reset(formdata);

    const adminAccessValue = formdata.isBankUser ? "true" : "false";
    setValue("admin_access", adminAccessValue);
    setAdminAccess(adminAccessValue);

    if (formdata.user_role_map && formdata.user_role_map.length > 0) {
      const firstRole = formdata.user_role_map[0].roleEntity;
      setValue("role_list", firstRole.id.toString());
    }

    if (formdata.bankId) {
      setValue("all_banks", formdata.bankId.toString());

      if (formdata.branchId && adminAccessValue === "false") {
        setValue("all_branches", formdata.branchId.toString());
        await fetchBranchDetails(formdata.bankId);
      }
    }

    setTimeout(() => {
      if (
        formdata.user_department_map &&
        formdata.user_department_map.length > 0
      ) {
        const departmentId = formdata.user_department_map[0].department_id;
        console.log("Setting department ID:", departmentId);
        setValue("department", departmentId.toString(), {
          shouldValidate: true,
        });
      }

      if (formdata.jobTitleId) {
        setValue("jobTitleName", formdata.jobTitleId.toString(), {
          shouldValidate: true,
        });
      }

      if (formdata.userLevel) {
        setValue("userLevel", formdata.userLevel, {
          shouldValidate: true,
        });
      }
    }, 500);

    if (formdata.user_region_map && formdata.user_region_map.length > 0) {
      const selectedRegions = formdata.user_region_map.map((user) =>
        user.region_id.toString()
      );

      setTimeout(() => {
        setValue("region", selectedRegions, { shouldValidate: true });

        // FIXED: Find region field by name
        const regionFieldIndex = getFieldIndexByName('region');
        const regionDataTemp = CreateUserConfig[0].fields[regionFieldIndex].options;
        if (regionDataTemp && regionDataTemp.length > 0) {
          const regionDataWthSlctdValus = addSelectedFlag(
            [regionDataTemp],
            selectedRegions
          );
          setRegionData(regionDataWthSlctdValus);
          CreateUserConfig[0].fields[regionFieldIndex]["options"] =
            regionDataWthSlctdValus[0];
          setUserConfig([...CreateUserConfig]);
        }
      }, 500);
    }
    
    const updatedConfig = [...CreateUserConfig];
    // FIXED: Find branch field by name
    const branchFieldIndex = getFieldIndexByName('all_branches');
    updatedConfig[0].fields[branchFieldIndex]["disabled"] = adminAccessValue === "true";
    setUserConfig(updatedConfig);
  };

  function addSelectedFlag(data, selectedValues) {
    return data.map((item) => {
      if (Array.isArray(item)) {
        return addSelectedFlag(item, selectedValues);
      }

      const updatedItem = { ...item };

      if (selectedValues.includes(updatedItem.id?.toString())) {
        updatedItem.checked = true;
      }

      if (updatedItem.children && updatedItem.children.length > 0) {
        updatedItem.children = addSelectedFlag(
          updatedItem.children,
          selectedValues
        );
      }

      return updatedItem;
    });
  }

  // Returns true only when the User ID is free ("success"); shows a message otherwise
  const validateUsername = async (username) => {
    setLoader(true);
    const response = await getData(
      validateUserNames.replace("USERNAME", encodeURIComponent(username))
    );
    if (response?.status === "success") {
      return true;
    }
    if (response?.status === "failure") {
      toast.error(response.msg || "User ID already exists. Please choose a different User ID.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
      return false;
    }
    // getData returns [] on network/server errors — don't treat that as "taken"
    toast.error("Unable to validate the User ID. Please try again.", {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 3000,
    });
    return false;
  };

  const onHierDropdownChange = (currentNode, selectedNodes) => {
    isRegionChanged(true);
    let regionDataSlected = [];
    selectedNodes.forEach((region) => {
      regionDataSlected.push({ region_id: parseInt(region.id) });
    });
    setRegionDataFrUpsert(regionDataSlected);

    setValue("region", selectedNodes.length > 0 ? selectedNodes : null, {
      shouldValidate: true,
      shouldDirty: true,
    });

    clearErrors("region");
  };

  // FIXED: getAllDepartmentList function
  const getAllDepartmentList = async () => {
    try {
      const departmentList = await getData(departmentslist);
      const formattedDepartments = departmentList.map((dept) => ({
        value: dept.id,
        label: dept.name,
      }));

      const departmentsWithDefault = [
        { value: "", label: "Select a Department" },
        ...formattedDepartments,
      ];
      
      const updatedConfig = [...CreateUserConfig];
      // FIXED: Find department field by name
      const departmentFieldIndex = getFieldIndexByName('department');
      updatedConfig[0].fields[departmentFieldIndex]["options"] = departmentsWithDefault;
      setUserConfig(updatedConfig);
    } catch (error) {
      console.error("Error fetching departments:", error);
      toast.error("Failed to fetch departments", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  const jobHandler = (props) => {
    setJobPopUp(!jobPopUp);
  };

  const handleAddJobTitle = async (jobTitle) => {
    await createJobTiltle(jobTitle);
    setJobPopUp(false);
  };

  function showSuccessMsg(configId) {
    toast.success("Job title added successfully.", {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
    });
  }

  const GenerateForm = (fields, register, errors, control) => {
    // Bank Users cover the whole bank — hide Branch Name; show it only for Branch Users
    const visibleFields =
      adminAccess === "true"
        ? fields.filter((field) => field.name !== "all_branches")
        : fields;
    return (
      <div>
        {regionData.length > 0 && (
          <FormHandler
            register={register}
            errors={errors}
            fields={visibleFields}
            onChange={onHierDropdownChange}
            control={control}
            columns={2}
            getValues={getValues}
            isBankFieldDisable={isBankFieldDisable}
            isBranchNameFieldDisable={adminAccess === "true"}
            makeRadioButtonForBranchUserDisable={
              makeRadioButtonForBranchUserDisable || isAdminRole
            }
            roleTrue={roleTrue}
            userNameDisable={userNameDisable}
            userLevelDisabled={userLevelDisabled}
          />
        )}
      </div>
    );
  };

  const generateTabsData = (tabWiseData) => {
    tabWiseData?.forEach((element) => {
      return (element.component = GenerateForm(
        element?.fields,
        register,
        errors,
        control
      ));
    });
    return tabWiseData;
  };

  const getLoggedInUserAuthToken = async () => {
    return keycloak.token;
  };

  const getInputFrURM = (data) => {
    let inputobj = {
      firstName: data.first_name,
      lastName: data.last_name,
      emailVerified: true,
      enabled: true,
      email: toUserEmail(data.user_name),
      username: data.user_name,
      attributes: {
        address: [data.address],
        phone1: [data.phone_number],
        phone2: [data.secondary_phone_number],
        middleName: [data.middle_name],
      },
    };
    if (!dataFrEdit) {
      inputobj["credentials"] = [
        {
          type: "password",
          value: process.env.REACT_APP_NEW_USER_DEFAULT_PASSWORD,
          temporary: true,
        },
      ];
    }

    return inputobj;
  };

  const getURMHeader = async () => {
    const authToknObj = await getLoggedInUserAuthToken();
    let headers = {
      "content-type": "application/json",
      Authorization: "Bearer " + authToknObj,
    };
    return headers;
  };

  const getUserDataByEmail = async (email) => {
    let url =
      keycloakbaseurl +
      getUserDataByEmailAttr
        .replace("{REALM}", process.env.REACT_APP_URM_REALM)
        .replace("{EMAIL}", email);
    const authToknObj = await getLoggedInUserAuthToken();
    let headers = {
      "content-type": "application/json",
      Authorization: "Bearer " + authToknObj,
    };
    const response = await api.getData(url, null, headers);

    return response?.data[0]?.id;
  };

  const assignRolesToTheUserInApp = async (data, inputobj) => {
    let headers = {
      "content-type": "application/json",
    };
    return await postDataWithBody(
      assignRoleToUser,
      getUserInput(data),
      headers
    );
  };

  const updateUserInApp = async (data, inputobj) => {
    let headers = {
      "content-type": "application/json",
    };
    let userInput = getUserInput(data);
    await putDataWithBody(
      updateUserInAppURL,
      JSON.stringify(userInput),
      headers
    );
  };

  const getUserInput = (data) => {
    var rolesTobEAssigned = [];

    if (data.role_list) {
      let roleId = null;

      if (typeof data.role_list === "object" && data.role_list.app_role_id) {
        roleId = data.role_list.app_role_id;
      } else if (typeof data.role_list === "string") {
        roleId = data.role_list;
      } else if (Array.isArray(data.role_list) && data.role_list.length > 0) {
        const role = data.role_list[0];
        roleId = typeof role === "object" ? role.app_role_id : role;
      }

      if (roleId) {
        rolesTobEAssigned.push({ role_id: roleId });
      }
    }

    data["user_region_map"] =
      regionChanged === true ? regionDataFrUpsert : data.user_region_map;

    data["user_role_map"] = rolesTobEAssigned;

    const selectedBankId = data.all_banks;
    const selectedBranchId = data.all_branches;
    const isAdminAccess = data.admin_access === "true";

    let bankId = null;
    let branchId = null;

    if (selectedBankId) {
      bankId = parseInt(selectedBankId);
      data["bankId"] = bankId;

      if (!selectedBranchId || isAdminAccess) {
        branchId = null;
        data["branchId"] = null;
        data["isBankUser"] = true;
        data["isBranchUser"] = false;
      } else if (selectedBranchId && !isAdminAccess) {
        branchId = parseInt(selectedBranchId);
        data["branchId"] = branchId;
        data["isBankUser"] = false;
        data["isBranchUser"] = true;
      }
    } else {
      data["bankId"] = null;
      data["branchId"] = null;
      data["isBankUser"] = false;
      data["isBranchUser"] = false;
    }

    if (data.department) {
      const departmentId = parseInt(data.department);
      data["departmentId"] = departmentId;

      data["user_department_map"] = [
        {
          department_id: departmentId,
          bank_id: bankId,
          branch_id: branchId,
        },
      ];
    } else {
      data["user_department_map"] = [];
    }

    if (data.jobTitleName) {
      data["jobTitleId"] = parseInt(data.jobTitleName);
    }

    if (data.userLevel && data.userLevel !== "") {
    } else {
      data.userLevel = null;
    }

    data["appType"] = appType;

    return data;
  };

  const updateUserInURM = async (inputobj) => {
    var userId = await getUserDataByEmail(toUserEmail(inputobj.username));
    let url =
      keycloakbaseurl +
      updateUserByUserId.replace("{REALM}", process.env.REACT_APP_URM_REALM) +
      userId;
    let headers = await getURMHeader();
    try {
      const response = await api.putData(
        url,
        headers,
        JSON.stringify(inputobj)
      );
      if (response.status === 204) {
        toast.success("User Updated Successfully", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        return true;
      } else {
        toast.error("Unable to update the user.", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        return false;
      }
    } catch (e) {
      toast.error(e.response.data.errorMessage, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return false;
    }
  };

  const createUserInURM = async (inputobj) => {
    let url =
      keycloakbaseurl +
      createUserintoRealm.replace("{REALM}", process.env.REACT_APP_URM_REALM);
    let headers = await getURMHeader();

    try {
      const response = await api.postData(
        url,
        headers,
        JSON.stringify(inputobj)
      );

      if (response.status === 201) {
        const authToken = await getLoggedInUserAuthToken();
        const rolesResult = await createUserWithRoles(inputobj, authToken);

        if (rolesResult.success) {
          toast.success("User details saved successfully", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
          return true;
        } else {
          toast.warning(
            "User created but role assignment failed: " + rolesResult.error,
            {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 3000,
            }
          );
          return true;
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.errorMessage || "Failed to create user", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      setLoader(false);
      return false;
    }
  };

  const createUserWithRoles = async (userData, accessToken) => {
    const baseUrl = "https://convergeiccc.com/urm/admin/realms/Converge";

    const REALM_MANAGEMENT_CLIENT_ID = process.env.REACT_APP_REAL_URM_CLIENT_ID;

    const ROLE_IDS = {
      "manage-users": process.env.REACT_APP_REAL_URM_USERS,
      "manage-realm": process.env.REACT_APP_REAL_URM_REALM,
    };

    try {
      const usersResponse = await fetch(
        `${baseUrl}/users?username=${userData.username}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      const users = await usersResponse.json();
      const userId = users[0].id;

      await fetch(
        `${baseUrl}/users/${userId}/role-mappings/clients/${REALM_MANAGEMENT_CLIENT_ID}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify([
            {
              id: ROLE_IDS["manage-users"],
              name: "manage-users",
            },
            {
              id: ROLE_IDS["manage-realm"],
              name: "manage-realm",
            },
          ]),
        }
      );

      return { success: true, userId };
    } catch (error) {
      console.error("Error:", error);
      return { success: false, error: error.message };
    }
  };

  const submitHandler = async (data, e) => {
    e.preventDefault();
    const isValid = await trigger();
    if (!isValid) return;

    data["status"] = true;
    setLoader(true);
    if (dataFrEdit) {
      let inputobj = getInputFrURM(data);
      let isUserUpdatedInURM = await updateUserInURM(inputobj);
      if (isUserUpdatedInURM === true) {
        await updateUserInApp(data, inputobj);
        setLoader(false);
        navigate("/users-management/users");
      }
    } else {
      // Create only if the User ID is available
      const isUsernameValid = await validateUsername(data.user_name);
      if (!isUsernameValid) {
        setLoader(false);
        return;
      }
      let inputobj = getInputFrURM(data);
      data["email"] = toUserEmail(inputobj.username);
      let isUserCreatedInURM = await createUserInURM(inputobj);
      if (isUserCreatedInURM === true) {
        await assignRolesToTheUserInApp(data, inputobj);
        setLoader(false);
        navigate("/users-management/users");
      }
    }
  };

  const saveTabDataHandler = async (e) => {
    const isStepValid = await trigger();
    if (isStepValid) setActiveTab((prevId) => prevId + 1);
  };

  const previousHandler = () => {
    if (activeTab > 0) {
      setActiveTab((prevId) => prevId - 1);
    }
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    reset();
    setRegionDataFrUpsert([]);
    setActiveTab(0);
    setShowCancelModal(false);
    setComponentKey((prevKey) => prevKey + 1);
  };

  const tabsData = generateTabsData(userConfig);

  return (
    <div className="mt-4">
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Data"
      />
      <div className="form-body">
        <div className="registration__form--body">
          <form
            key={componentKey}
            onSubmit={handleSubmit(submitHandler)}
            name="AgancyRegForm"
          >
            <div>
              {tabsData?.map((tab) => (
                <>
                  {tab.id === activeTab && (
                    <>
                      <div className="registration__form--heading">
                        {tab.heading}
                      </div>
                      <div>{tab.component}</div>
                    </>
                  )}
                </>
              ))}
            </div>
            <div
              className="form-submit-container full-width-field"
              style={{ marginTop: "50px" }}
            >
              {activeTab > 0 && (
                <button
                  type="button"
                  className="branch-save-submit-btn btn btn-success"
                  onClick={previousHandler}
                  style={{
                    backgroundColor: "#1a7a73",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    marginRight: "10px",
                  }}
                >
                  Previous
                </button>
              )}
              {tabsData.length - 1 === activeTab && (
                <>
                  <button
                    type="submit"
                    className="branch-save-submit-btn btn btn-success"
                    style={{
                      backgroundColor: "#1a7a73",
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      marginRight: "10px",
                    }}
                  >
                    {edit ? t("Update") : t("saveAndSubmit")}
                  </button>
                  {!edit && (
                    <button
                      type="button"
                      className="branch-save-submit-btn btn btn-success"
                      onClick={handleCancelClick}
                    >
                      Cancel
                    </button>
                  )}
                </>
              )}
              {tabsData.length - 1 !== activeTab && (
                <button
                  type="button"
                  className="branch-save-submit-btn btn btn-success"
                  onClick={saveTabDataHandler}
                >
                  save & Next
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
      {jobPopUp && (
        <ModalResourceType
          titleValue={t("popup.title")}
          showSuccessMsg={showSuccessMsg}
          onAddJobTitle={handleAddJobTitle}
          show={jobPopUp}
          buttonLabel={t("popup.button")}
          handleToggle={jobHandler}
          popupLabel={t("popup.label")}
          {...props}
        />
      )}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm </Modal.Title>
        </Modal.Header>
        <Modal.Body>Would you like to clear the input values?</Modal.Body>
        <Modal.Footer>
          <button
            className="btn"
            style={{
              backgroundColor: "lightgray",
              color: "#000",
              marginRight: "10px",
            }}
            onClick={() => setShowCancelModal(false)}
          >
            No
          </button>
          <button
            className="btn"
            style={{
              backgroundColor: "green",
              color: "#fff",
            }}
            onClick={handleConfirmCancel}
          >
            Yes
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default UserRegistrationForm;
